// Patient-view configuration shared across Builder, Present Mode, and the
// patient-at-home view.
//
// There are two patient views: Arch & Region and Itemized. (Phase Journey
// was removed.) The practice controls, in Settings → General:
//   - enabledViews: which views the patient can see/toggle between
//   - defaultView:  which one opens first (only meaningful when 2 enabled)
//
// These helpers resolve the General settings blob into a clean enabled list
// and a valid default, coercing the legacy 'phase' value and guaranteeing at
// least one enabled view.

// Canonical tab order.
export const TX_VIEW_ORDER = ['region', 'itemized'];

export const TX_VIEW_LABELS = {
  region: 'Arch & Region',
  itemized: 'Itemized',
};

/** [{ id, label }] in canonical order — for rendering per-view control rows. */
export const TX_VIEW_CHOICES = TX_VIEW_ORDER.map((id) => ({ id, label: TX_VIEW_LABELS[id] }));

/**
 * The per-view pricing modes, with the copy used in every toggle surface
 * (Builder Financials tab, chairside TC tray, practice Settings).
 *
 * Defined once because the wording is the feature: a client reported that the
 * two views were indistinguishable on price and that the layer toggles moved
 * both at once. "Total only" is the answer, and it has to read the same way
 * wherever a TC meets it.
 */
export const TX_PRICING_MODE_OPTIONS = [
  { id: 'full', label: 'Full pricing', hint: 'Per-procedure fees plus the Plan Summary total.' },
  { id: 'total', label: 'Total only', hint: 'No per-procedure fees — just the Plan Summary total at the bottom.' },
  { id: 'none', label: 'No pricing', hint: 'No dollar figures anywhere in this view.' },
];

/**
 * Returns the enabled view ids in canonical order. An unset enabledViews
 * (legacy plans / fresh practices) defaults to BOTH views on. Never returns
 * an empty array — falls back to ['region'] if a practice somehow disabled
 * everything.
 */
export function resolveEnabledViews(general) {
  const ev = (general && general.enabledViews) || {};
  const enabled = TX_VIEW_ORDER.filter((id) => ev[id] !== false);
  return enabled.length ? enabled : ['region'];
}

/**
 * Returns a valid default view id: the practice's defaultView when it's
 * among the enabled views, otherwise the first enabled view. Legacy
 * 'phase' is coerced to 'region'.
 */
export function resolveDefaultView(general) {
  const enabled = resolveEnabledViews(general);
  let dv = (general && general.defaultView) || 'region';
  if (dv === 'phase') dv = 'region';
  return enabled.includes(dv) ? dv : enabled[0];
}
